<template>
  <div class="passenger-form">
    <toolbar
      backRoute="/dashboard/passengers"
      :hideBackButton="isPopup"
      :beforeBack="checkForChanges"
      :title="isPopup ? '' : toolbarTitle"
      :deleteButtonLabel="deleteButtonLabel"
      deleteButtonEvent="passenger-item-delete"
      :saveButtonLabel="isPopup ? 'Add new passenger' : 'Save passenger'"
      saveButtonEvent="passenger-item-save"
      :deleteInProgress="removeInProgress"
      :saveInProgress="saveInProgress" />

    <d-container fluid class="common-form px-0 pt-5 pb-5">
      <d-form>
        <validation-observer ref="passenger-form" tag="form" v-if="!loadError && allDataLoaded">
          <d-row>
            <d-col cols="6">
              <div class="form-group">
                <label for="first_name">First name:</label>

                <validation-provider
                  rules="required"
                  name="first_name"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.first_name"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="first_name">Last name:</label>

                <validation-provider
                  rules="required"
                  name="last_name"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.last_name"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="birth_date">DOB:</label>

                <d-datepicker
                  v-model="data.birth_date"
                  format="dd / MM / yyyy"
                  placeholder="Select a date" />
              </div>

              <div class="form-group">
                <label for="birth_place">Birth place:</label>
                <d-input v-model="data.birth_place" />
              </div>

              <div class="form-group">
                <label for="uk_address">UK Address:</label>
                <d-input v-model="data.uk_address" />
              </div>

              <div class="form-group">
                <label for="sex">Sex:</label>

                <div>
                  <d-form-radio inline name="sex" v-model="data.sex" value="M">Male</d-form-radio>
                  <d-form-radio inline name="sex" v-model="data.sex" value="F">Female</d-form-radio>
                </div>
              </div>

              <div class="form-group">
                <label for="rewards">Rewards:</label>

                <rewards-list
                  v-if="loadedData"
                  referenceType="PASSENGER"
                  :referenceId="data.id"
                  itemName="passenger" />
              </div>
            </d-col>

            <d-col cols="6">
              <div class="form-group">
                <label for="known_as">Known as:</label>
                <d-input v-model="data.known_as" />
              </div>

              <div class="form-group">
                <label>Customer:</label>

                <validation-provider
                  rules="required"
                  name="flight_customer_id"
                  v-slot="{ errors }">
                  <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                    <v-select
                      :options="operators"
                      v-model="data.flight_customer_id"
                      :reduce="customer => customer.id"
                      :filterBy="filterByCustomers"
                      :maxItemsDisplay="100"
                      :disabled="!!customerID"
                      placeholder="Select a customer"
                      label="id">
                      <template slot="no-options">
                        <template>There are no customers for the provided phrase&hellip;</template>
                      </template>
                      <template slot="option" slot-scope="option">
                        <div class="d-center">
                          {{ option.name }}
                        </div>
                      </template>
                      <template slot="selected-option" slot-scope="option">
                        <div class="selected d-center">
                          {{ option.name }}
                        </div>
                      </template>
                    </v-select>
                  </div>

                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="notes">Notes:</label>
                <d-textarea
                  v-model="data.notes"
                  :value.prop="data.notes"
                  :rows="10" />
              </div>

              <div class="form-group">
                <label for="phone">Phone:</label>
                <d-input v-model="data.phone" />
              </div>

              <div class="form-group">
                <label for="email">E-mail:</label>
                <d-input v-model="data.email" />
              </div>
            </d-col>
          </d-row>
        </validation-observer>

        <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
          Loading data&hellip;
        </div>

        <d-alert
          v-if="loadError"
          show
          theme="warning">
          The app were unable to load data. <a href="javascript:window.location.reload();" class="alert-link">Refresh the app</a> to try again.
        </d-alert>
      </d-form>

      <documents-list
        v-if="loadedData && !isPopup"
        referenceType="PASSENGER"
        :referenceId="data.id"
        itemName="passenger"
        itemType="passports" />

      <documents-list
        v-if="loadedData && !isPopup"
        referenceType="PASSENGER"
        :referenceId="data.id"
        itemName="passenger"
        itemType="documents-licences" />

      <warnings-list
        v-if="loadedData && !isPopup"
        referenceType="PASSENGER"
        :referenceId="data.id"
        itemName="passenger" />

      <visas-list
        v-if="loadedData && !isPopup"
        referenceType="PASSENGER"
        :referenceId="data.id"
        itemName="passenger" />

      <documents-list
        v-if="loadedData && !isPopup"
        referenceType="PASSENGER"
        :referenceId="data.id"
        itemName="passenger"
        itemType="documents" />

      <checklist-list
        v-if="loadedData && !isPopup"
        referenceType="PASSENGER"
        :referenceId="data.id"
        itemName="passenger" />
    </d-container>
  </div>
</template>

<script>
import ChecklistList from './../form-parts/CheckLists.vue';
import DateUtils from './../../utils/DateUtils.js';
import DocumentsList from './../form-parts/Documents.vue';
import FormUtils from './../../utils/FormUtils.js';
import HasChangesDetection from './../../mixins/HasChangesDetection.vue';
import ListUtils from './../../utils/ListUtils.js';
import VisasList from './../form-parts/Visas.vue';
import RewardsList from './../form-parts/Rewards.vue';
import WarningsList from './../form-parts/Warnings.vue';
import DOMPurify from 'dompurify';
import Vue from 'vue';

export default {
  name: 'passenger-form',
  mixins: [
    HasChangesDetection
  ],
  props: {
    isPopup: {
      default: false
    },
    customerID: {
      default: null
    }
  },
  components: {
    'checklist-list': ChecklistList,
    'documents-list': DocumentsList,
    'rewards-list': RewardsList,
    'visas-list': VisasList,
    'warnings-list': WarningsList
  },
  computed: {
    allDataLoaded () {
      return this.loadedData && this.loadedOperators;
    },
    isEdit () {
      return this.$route.params.id !== 'new' && parseInt(this.$route.params.id, 10) > 0;
    },
    toolbarTitle () {
      if (!this.isEdit) {
        return 'Add new passenger';
      }

      return 'Edit passenger';
    },
    deleteButtonLabel () {
      if (this.$route.params.id === 'new' || this.isPopup) {
        return false;
      }

      return 'Delete passenger';
    }
  },
  data () {
    return {
      data: {
        id: 0,
        first_name: '',
        last_name: '',
        birth_date: '',
        known_as: '',
        notes: '',
        phone: '',
        email: '',
        birth_place: '',
        uk_address: '',
        sex: 'M',
        flight_customer_id: this.customerID
      },
      operators: [],
      loadedOperators: false,
      loadError: false,
      loadedData: false,
      removeInProgress: false,
      saveInProgress: false
    };
  },
  mounted () {
    if (!this.isEdit) {
      this.loadedData = true;
    } else {
      this.loadData();
    }

    this.loadAdditionalData();
    this.$bus.$on('passenger-item-delete', this.removePassenger);
    this.$bus.$on('passenger-item-save', this.validateForm);
  },
  methods: {
    loadData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/passengers/item/',
        instanceFields: ['id', 'first_name', 'last_name', 'birth_date', 'birth_place', 'uk_address', 'sex', 'known_as', 'notes', 'phone', 'email', 'flight_customer_id'],
        loadedKey: 'loadedData',
        successAction: () => {
          this.data = DateUtils.groupConversionStringToDate(this.data, ['birth_date']);
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/customers/items',
        params: {
          pagination: 0
        },
        loadedKey: 'loadedOperators',
        outputKey: 'operators',
        errorKey: 'loadError'
      });
    },
    removePassenger () {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/passengers/delete/',
        id: parseInt(this.$route.params.id, 10),
        successTitle: 'Passenger removed',
        successText: 'The selected passenger has been removed',
        confirmTitle: 'Do you really want to remove this passenger?',
        confirmText: 'This operation cannot be reverted',
        errorTitle: 'An error occurred',
        errorText: 'Please try again.',
        successAction: () => {
          this.removeInProgress = false;
          this.$router.push('/dashboard/passengers');
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['passenger-form'], this.checkSimiliarItems);
    },
    checkSimiliarItems () {
      if (this.id > 0) {
        this.submitForm();
        return;
      }

      FormUtils.submitInBackground({
        $http: this.$http,
        formData: {
          order: {
            column: 'last_name',
            direction: 'ASC'
          },
          where: {
            last_name: this.data.last_name
          }
        },
        endpoint: '/passengers/items?page=1',
        errorTitle: 'An error occurred',
        errorText: 'An error occurred during checking data before save. Please try again',
        successAction: (response) => {
          if (!response.data.length) {
            this.submitForm();
            return;
          }

          let listOfItems = response.data.map(person => {
            let output = '<div>';
            output += '<strong>' + person.first_name + ' ' + person.last_name + '</strong>';
            output += ' - ';
            output += person.birth_date;

            if (person.birth_place) {
              output += ' (' + person.birth_place + ')';
            }

            return output + '</div>';
          });

          Vue.swal({
            title: 'Passengers with similiar last name exist',
            html: DOMPurify.sanitize('<div class="swal-people-list">' + listOfItems + '</div>'),
            icon: 'question',
            confirmButtonText: 'Save it anyway',
            cancelButtonText: 'Cancel',
            showCancelButton: true,
            buttonsStyling: true
          }).then((result) => {
            if (result.value) {
              this.submitForm();
            }
          });
        }
      });
    },
    submitForm () {
      let endpointUrl = '/passengers/store';
      let mode = 'add';

      if (this.id > 0) {
        endpointUrl = '/passengers/update/' + parseInt(this.id, 10);
        mode = 'edit';
      }

      this.data = DateUtils.groupConversionDateToString(this.data, ['birth_date']);

      FormUtils.submit({
        $http: this.$http,
        endpoint: endpointUrl,
        formData: this.data,
        successTitle: mode === 'add' ? 'Passenger added' : 'Passenger saved',
        successText: 'The passenger has been successfully saved!',
        errorTitle: 'An error occurred!',
        errorText: 'The operation not finished successfully. Please try again.',
        successAction: (response) => {
          if (this.isPopup) {
            this.$bus.$emit('add-passenger-popup-close');
            this.$bus.$emit('pax-list-passengers-reload-needed');

            if (response.item) {
              this.$bus.$emit('show-document-popup', 'add', 'PASSENGER', response.item.id, false, 'passports', true);
            }
          } else {
            this.$router.push('/dashboard/passengers');
          }
        }
      });
    },
    filterByCustomers (option, label, search) {
      return option.name.toLowerCase().indexOf(search.toLowerCase()) > -1;
    }
  },
  beforeDestroy () {
    this.$bus.$off('passenger-item-delete', this.removePassenger);
    this.$bus.$off('passenger-item-save', this.validateForm);
  }
}
</script>
